import React, { useState } from 'react';
import { Heart, Check, ShieldCheck, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Periodic, opt-out-able injury review. Shown when the backend says
// `injury-review-status.due === true`. We deliberately do NOT offer a
// per-exercise "ignore safety swap" toggle — that would erode the contract
// the user opted into. Instead, this sheet asks once, globally, about each
// injury chip. One conscious decision beats five impulsive overrides.
//
// Each injury can be: kept (still healing) or healed (remove the chip).
// Default is "kept" — we never silently remove anything.
export default function InjuryReviewSheet({ injuries = [], daysSince, onClose, onUpdated }) {
  const { toast } = useToast();
  const [decisions, setDecisions] = useState(
    () => Object.fromEntries(injuries.map((i) => [i, 'kept']))
  );
  const [submitting, setSubmitting] = useState(false);

  const setChoice = (injury, choice) =>
    setDecisions((d) => ({ ...d, [injury]: choice }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const kept = injuries.filter((i) => decisions[i] === 'kept');
      const healed = injuries.filter((i) => decisions[i] === 'healed');
      const res = await api.submitInjuryReview(kept, healed);
      if (healed.length) {
        toast({
          title: `Healed: ${healed.join(', ')}`,
          description: 'Future workouts will load those movements back in.',
        });
      } else {
        toast({
          title: 'Got it — protecting them all',
          description: `We\u2019ll check back in 14 days.`,
        });
      }
      onUpdated && onUpdated(res);
      onClose && onClose();
    } catch (e) {
      toast({ title: 'Could not save', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="injury-review-sheet"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0f1115] border-t sm:border border-zinc-800 sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#BEFF38]/10 border border-[#BEFF38]/30 flex items-center justify-center shrink-0">
            <Heart className="w-5 h-5 text-[#BEFF38]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-white font-black text-lg leading-tight" data-testid="injury-review-title">
              Quick check-in
            </h2>
            <p className="text-zinc-400 text-xs mt-0.5">
              {daysSince >= 9999
                ? 'First time reviewing your injury list.'
                : `It\u2019s been ${daysSince} days since we last asked.`} Anything healed?
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {injuries.map((injury) => {
            const choice = decisions[injury];
            return (
              <div
                key={injury}
                data-testid={`injury-review-row-${injury.toLowerCase().replace(/\s+/g, '-')}`}
                className="rounded-lg border border-zinc-800 bg-[#0b0c10] p-3"
              >
                <p className="text-white font-bold text-sm mb-2">{injury}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setChoice(injury, 'kept')}
                    data-testid={`injury-keep-${injury.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex-1 px-3 h-9 rounded-lg text-[11px] font-black uppercase tracking-wide border transition-colors ${
                      choice === 'kept'
                        ? 'bg-[#ff7a1a]/10 border-[#ff7a1a] text-[#ff7a1a]'
                        : 'bg-transparent border-zinc-800 text-zinc-500 hover:text-zinc-300'
                    }`}
                  >
                    Still healing
                  </button>
                  <button
                    type="button"
                    onClick={() => setChoice(injury, 'healed')}
                    data-testid={`injury-healed-${injury.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex-1 px-3 h-9 rounded-lg text-[11px] font-black uppercase tracking-wide border transition-colors ${
                      choice === 'healed'
                        ? 'bg-[#BEFF38]/15 border-[#BEFF38] text-[#BEFF38]'
                        : 'bg-transparent border-zinc-800 text-zinc-500 hover:text-zinc-300'
                    }`}
                  >
                    Healed
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-lg bg-zinc-900/60 border border-zinc-800 px-3 py-2 mb-4">
          <p className="text-[10px] text-zinc-500 leading-snug flex items-start gap-1.5">
            <ShieldCheck className="w-3 h-3 text-[#BEFF38] mt-0.5 shrink-0" />
            <span>
              If you\u2019re not sure, leave it as <span className="text-zinc-300 font-semibold">Still healing</span>.
              We\u2019d rather over-protect than rush you back.
            </span>
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="injury-review-skip"
            disabled={submitting}
            className="px-4 h-11 rounded-full bg-transparent border border-zinc-800 text-zinc-400 text-[11px] font-black uppercase tracking-wider hover:text-white disabled:opacity-50"
          >
            Ask me later
          </button>
          <button
            type="button"
            onClick={submit}
            data-testid="injury-review-submit"
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 h-11 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-[#a6e62e] disabled:opacity-50"
          >
            {submitting ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Check className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
